<template>
  <div class="infinite-scroller" :dir="dir" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <div ref="trackRef" class="infinite-scroller-track" :class="{ 'is-paused': isPaused }" :style="trackStyle">
      <div v-for="n in 2" :key="n" ref="setRefs" class="infinite-scroller-set" :aria-hidden="n === 2 ? 'true' : undefined">
        <a v-for="(item, index) in items" :key="`${n}-${index}`" :href="item.link" class="business-app-item" :tabindex="n === 2 ? -1 : 0">
          <div class="business-app-item-icon">
            <img v-if="item.image" :src="item.image" :alt="item.title" class="business-app-item-img" />
            <img v-else-if="isImageIcon(item.icon)" :src="item.icon" :alt="item.title" class="business-app-item-img" />
            <icon v-else-if="item.icon" :icon="item.icon" />
          </div>
          <div class="business-app-item-name">{{ item.title }}</div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

  const props = defineProps({
    items: {
      type: Array,
      required: true,
    },
    speed: {
      type: Number,
      default: 30,
    },
    dir: {
      type: String,
      default: 'ltr',
      validator: (v) => ['rtl', 'ltr'].includes(v),
    },
    pauseOnHover: {
      type: Boolean,
      default: true,
    },
    gap: {
      type: String,
      default: '32px',
    },
  })

  function isImageIcon(val) {
    if (typeof val !== 'string') return false
    return val.startsWith('data:') || val.startsWith('/') || val.startsWith('http') || /\.(webp|png|jpg|jpeg|svg|gif)$/i.test(val)
  }

  const trackRef = ref(null)
  const setRefs = ref([])
  const setWidth = ref(0)
  const isPaused = ref(false)
  const hovering = ref(false)

  let resizeObserver = null

  const duration = computed(() => {
    if (!setWidth.value || !props.speed) return 0
    return setWidth.value / props.speed // seconds
  })

  const trackStyle = computed(() => ({
    '--gap': props.gap,
    '--duration': `${duration.value}s`,
    // animate toward the right for rtl, toward the left for ltr
    '--travel-x': props.dir === 'rtl' ? '50%' : '-50%',
  }))

  function measure() {
    // measure just the first set, since both sets are identical width
    const el = Array.isArray(setRefs.value) ? setRefs.value[0] : null
    if (el) {
      setWidth.value = el.scrollWidth
    }
  }

  function onMouseEnter() {
    if (props.pauseOnHover) {
      hovering.value = true
      isPaused.value = true
    }
  }

  function onMouseLeave() {
    hovering.value = false
    isPaused.value = false
  }

  onMounted(async () => {
    await nextTick()
    measure()

    resizeObserver = new ResizeObserver(() => measure())
    if (trackRef.value) resizeObserver.observe(trackRef.value)
  })

  onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect()
  })

  watch(
    () => props.items,
    async () => {
      await nextTick()
      measure()
    },
    { deep: true }
  )
</script>

<style scoped>
  .infinite-scroller {
    overflow: hidden;
    width: 100%;
  }

  .infinite-scroller-track {
    display: flex;
    width: max-content;
    animation-name: infinite-scroller-move;
    animation-duration: var(--duration, 30s);
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }

  .infinite-scroller-track.is-paused {
    animation-play-state: paused;
  }

  .infinite-scroller-set {
    display: flex;
    flex-shrink: 0;
    gap: var(--gap, 32px);
    padding-inline-end: var(--gap, 32px);
  }

  @keyframes infinite-scroller-move {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(var(--travel-x, -50%));
    }
  }

  .business-app-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-decoration: none;
    gap: 4px;
  }

  .business-app-item-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    color: #2a6cca;
    font-size: 32px;
  }

  .business-app-item-img {
    height: 40px;
    width: auto;
    max-width: 56px;
    object-fit: contain;
    display: block;
    user-select: none;
    pointer-events: none;
    transition: transform 0.25s ease;
  }

  .business-app-item:hover .business-app-item-img {
    transform: scale(1.08);
  }

  .business-app-item-name {
    color: #45556c;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  }
</style>